import type { EnergyProfile, IsoDate, Measurement, WorkoutClass } from "./types.js";
import { addDays, daysBetween } from "./dates.js";
import type { SeasonBaseline } from "./plan.js";
import { TARGET_END_DATE, TARGET_END_WEIGHT } from "./plan.js";
import { average, clamp, roundTo } from "./numbers.js";
import { latestMeasurement } from "./measurements.js";
import { workoutFor } from "./workout.js";

/**
 * BMR / TDEE / 建議攝取全部是估算值，不是實測消耗。
 * 專案刻意不做熱量打卡，這些數字只作為份量參考。見 CONTEXT.md「估算值」。
 */

export const DEFAULT_ENERGY_PROFILE: Readonly<EnergyProfile> = Object.freeze({
  sex: "male",
  age: 35,
  heightCm: 170,
  minIntake: 1700
});

/** 把來路不明的輸入夾成合理的身體資料。 */
export function normalizeProfile(raw: unknown): EnergyProfile {
  const saved = (raw && typeof raw === "object" ? raw : {}) as Partial<EnergyProfile>;
  return {
    ...DEFAULT_ENERGY_PROFILE,
    sex: saved.sex === "female" ? "female" : "male",
    age: clamp(Number(saved.age) || DEFAULT_ENERGY_PROFILE.age, 18, 90),
    heightCm: clamp(Number(saved.heightCm) || DEFAULT_ENERGY_PROFILE.heightCm, 130, 220)
  };
}

export interface WeightInfo {
  weight: number;
  source: "7日平均體重" | "最新體重" | "初始體重";
  date: IsoDate;
}

/**
 * 決定用哪個體重來估算。
 * 優先 7 日平均（至少 3 筆），其次最新體重，都沒有才用賽季起點。
 *
 * 連賽季起點都讀不到時用目標體重：算出來沒有赤字，往吃得夠的方向錯。
 */
export function energyWeightInfo(
  measurements: Measurement[],
  baseline: SeasonBaseline | null = null
): WeightInfo {
  const latest = latestMeasurement(measurements);
  if (!latest) {
    return baseline
      ? { weight: baseline.weight, source: "初始體重", date: baseline.date }
      : { weight: TARGET_END_WEIGHT, source: "初始體重", date: TARGET_END_DATE };
  }
  const start = addDays(latest.measurement_date, -6);
  const values = measurements
    .filter(item =>
      item.measurement_date >= start &&
      item.measurement_date <= latest.measurement_date &&
      Number.isFinite(item.weight_kg))
    .map(item => item.weight_kg);
  if (values.length >= 3) {
    return { weight: average(values) as number, source: "7日平均體重", date: latest.measurement_date };
  }
  return { weight: latest.weight_kg as number, source: "最新體重", date: latest.measurement_date };
}

export interface DayEnergyConfig {
  key: "rest" | "longrun" | "run" | "strength";
  label: string;
  /** 活動係數，乘上 BMR 得到 TDEE。 */
  factor: number;
  /** 該類日子容許的最大赤字，避免為了追進度過度節食。 */
  deficitCap: number;
  carbs: string;
  protein: string;
  vegetables: string;
}

const REST_CLASSES: WorkoutClass[] = ["rest", "recovery"];

/** 依當天課表類型決定活動係數與份量建議。forceRest 用於「沒完成課表」的情境。 */
export function energyDayConfig(date: IsoDate, forceRest = false): DayEnergyConfig {
  const [, , workoutClass] = workoutFor(date);

  if (forceRest || REST_CLASSES.includes(workoutClass)) {
    return { key: "rest", label: "休息／恢復日", factor: 1.35, deficitCap: 700, carbs: "2–2.5 拳", protein: "3–4 掌心", vegetables: "至少 4 拳" };
  }
  if (workoutClass === "longrun") {
    return { key: "longrun", label: "長跑日", factor: 1.60, deficitCap: 500, carbs: "4–5 拳", protein: "4 掌心", vegetables: "至少 4 拳" };
  }
  if (workoutClass === "run") {
    return { key: "run", label: "跑步日", factor: 1.50, deficitCap: 650, carbs: "3–4 拳", protein: "3–4 掌心", vegetables: "至少 4 拳" };
  }
  return { key: "strength", label: "重訓日", factor: 1.45, deficitCap: 700, carbs: "2.5–3.5 拳", protein: "4 掌心", vegetables: "至少 4 拳" };
}

/** Mifflin-St Jeor。 */
export function bmrEstimate(weight: number, profile: EnergyProfile): number {
  const sexConstant = profile.sex === "female" ? -161 : 5;
  return 10 * weight + 6.25 * profile.heightCm - 5 * profile.age + sexConstant;
}

export interface EnergyGuide {
  profile: EnergyProfile;
  weightInfo: WeightInfo;
  config: DayEnergyConfig;
  bmr: number;
  tdee: number;
  remaining: number;
  gap: number;
  requiredDeficit: number;
  plannedDeficit: number;
  low: number;
  high: number;
  /** 準時達標所需的赤字已經偏激進，介面會改口勸不要硬追。 */
  aggressive: boolean;
}

export interface EnergyGuideOptions {
  measurements: Measurement[];
  today: IsoDate;
  date: IsoDate;
  forceRest?: boolean;
  profile?: EnergyProfile;
  baseline?: SeasonBaseline | null;
}

/** 每 1 kg 脂肪約 7700 kcal，用來把「還差幾公斤」換算成每日赤字。 */
const KCAL_PER_KG = 7700;

export function energyGuideFor({
  measurements,
  today,
  date,
  forceRest = false,
  profile = { ...DEFAULT_ENERGY_PROFILE },
  baseline = null
}: EnergyGuideOptions): EnergyGuide {
  const weightInfo = energyWeightInfo(measurements, baseline);
  const config = energyDayConfig(date, forceRest);
  const bmr = bmrEstimate(weightInfo.weight, profile);
  const tdee = bmr * config.factor;

  const remaining = Math.max(1, daysBetween(today, TARGET_END_DATE));
  const gap = Math.max(0, weightInfo.weight - TARGET_END_WEIGHT);
  const requiredDeficit = gap > 0 ? gap * KCAL_PER_KG / remaining : 0;

  // 赤字有下限也有上限：不到 250 沒意義，超過 deficitCap 就是在硬追數字
  const plannedDeficit = gap <= 0 ? 0 : Math.min(config.deficitCap, Math.max(250, requiredDeficit));

  const center = Math.max(profile.minIntake, tdee - plannedDeficit);
  const low = Math.max(profile.minIntake, roundTo(center - 75));
  const high = Math.max(low + 100, roundTo(center + 75));

  return {
    profile, weightInfo, config, bmr, tdee,
    remaining, gap, requiredDeficit, plannedDeficit,
    low, high,
    aggressive: requiredDeficit > 700
  };
}
